import { all, takeEvery, put, call } from 'redux-saga/effects'
import { store as reduxStore } from 'index'
import { push } from 'react-router-redux'
import { message } from 'antd'

import appsPostOrDelete, {
  appsGetSettings,
  settingsUpdate,
  appsGetFeatures,
  settingsPostOrDelete,
} from 'services/ai'
import { settingsGet } from 'services/apps'

import actions from './actions'

/* UPDATE_SETTINGS */

export function* UPDATE_SETTINGS({ payload }) {
  try {
    yield put({
      type: 'ai/SET_STATE',
      payload: {
        loading: true,
      },
    })

    const activeApp = reduxStore.getState().apps.active
    const { updatedSettings, skipNavigation = false } = payload

    const success = yield call(settingsUpdate, activeApp, updatedSettings)

    if (success && success.data) {
      const aiSettings = yield call(settingsGet, activeApp.id, activeApp)

      if (!aiSettings.data.parameters['features.ai.enabled']) {
        yield call(settingsPostOrDelete, activeApp, { enabled: true })
        yield put({
          type: 'apps/GET_APP_SETTINGS',
          data: activeApp,
        })
        yield put({
          type: 'apps/SET_STATE',
          payload: {
            loading: false,
          },
        })
      }

      yield put({
        type: 'ai/GET_SETTINGS',
      })

      yield put({
        type: 'ai/SET_STATE',
        payload: {
          loading: false,
        },
      })

      // Only navigate if skipNavigation is false
      if (!skipNavigation) {
        yield put(push(`/app/${activeApp.id}/chat-settings`))
      }

      yield put({
        type: 'apps/INITIATE_NOTIFICATION',
        payload: { type: 'success', message: 'AI settings saved' },
      })
    } else {
      yield put({
        type: 'ai/SET_STATE',
        payload: {
          loading: false,
        },
      })
      if (success.error.code === 'ERR_BAD_REQUEST') {
        message.error(success.error.message)
      } else if (success.error.code === 'ERR_PLAN_RESTRICTION') {
        yield put({
          type: 'apps/INITIATE_NOTIFICATION',
          payload: {
            type: 'error',
            message: 'Plan restriction - Feature not available',
          },
        })
      } else if (success.error.message !== undefined) {
        yield put({
          type: 'apps/INITIATE_NOTIFICATION',
          payload: { type: 'error', message: success.error.message },
        })
      } else {
        yield put({
          type: 'apps/INITIATE_NOTIFICATION',
          payload: { type: 'error', message: 'Some error occurred' },
        })
      }
    }
    return success
  } catch (e) {
    yield put({
      type: 'ai/SET_STATE',
      payload: {
        loading: false,
      },
    })
    yield put({
      type: 'apps/INITIATE_NOTIFICATION',
      payload: { type: 'error', message: 'Some error occurred' },
    })
    console.log(e)
  }
}

/* GET_SETTINGS */

export function* GET_SETTINGS({ payload = {} }) {
  yield put({
    type: 'ai/SET_STATE',
    payload: {
      loading: true,
    },
  })

  if (!payload.data) payload.data = reduxStore.getState().apps.active

  const success = yield call(appsGetSettings, payload.data)
  if (success && success.data) {
    yield put({
      type: 'ai/SET_STATE',
      payload: {
        settingsActive: success.data,
        loading: false,
      },
    })
  } else {
    yield put(push(`/app/${payload.data.id}/chat-settings`))
  }
}

/* Ai Lists */

export function* AI_LIST({ payload }) {
  yield put({
    type: 'ai/SET_STATE',
    payload: {
      loading: true,
    },
  })

  if (!payload?.data) payload.data = reduxStore.getState().apps.active

  const success = yield call(appsGetFeatures, payload.data)
  if (success && success.data) {
    yield put({
      type: 'ai/SET_STATE',
      payload: {
        aiList: success.data,
        loading: false,
      },
    })
  }
}

/* update Ai feature */

export function* UPDATE_AI_FEATURE({ payload }) {
  yield put({
    type: 'apps/SET_STATE',
    payload: {
      loading: true,
    },
  })

  const activeApp = reduxStore.getState().apps.active
  const { updatedSettings, featureKey, featureLabel } = payload

  let successMessage

  if (updatedSettings.enabled) {
    successMessage = 'enabled'
  } else {
    successMessage = 'disabled'
  }

  const success = yield call(appsPostOrDelete, activeApp, updatedSettings, featureKey)

  if (success && success.data) {
    const settingSuccess = yield call(settingsGet, activeApp.id, activeApp)
    if (settingSuccess && settingSuccess.data) {
      yield put({
        type: 'apps/SET_STATE',
        payload: {
          appSettings: settingSuccess.data,
          loading: false,
        },
      })

      yield put({
        type: 'apps/INITIATE_NOTIFICATION',
        payload: { type: 'success', message: `${featureLabel} has been ${successMessage}.` },
      })
    }
    yield put({
      type: 'apps/SET_STATE',
      payload: {
        loading: false,
      },
    })
  } else {
    yield put({
      type: 'apps/SET_STATE',
      payload: {
        loading: false,
      },
    })
    if (success.error.code === 'ERR_BAD_REQUEST') {
      message.error(success.error.message)
    } else if (success.error.code === 'ERR_PLAN_RESTRICTION') {
      yield put({
        type: 'apps/INITIATE_NOTIFICATION',
        payload: {
          type: 'error',
          message: 'Plan restriction - Feature not available',
        },
      })
    } else if (success.error.message !== undefined) {
      yield put({
        type: 'apps/INITIATE_NOTIFICATION',
        payload: { type: 'error', message: success.error.message },
      })
    } else {
      yield put({
        type: 'apps/INITIATE_NOTIFICATION',
        payload: { type: 'error', message: 'Some error occurred' },
      })
    }
  }
  return success
}

// AI bots/instructions are handled by redux/customBots; this slice keeps only AI settings/features.
export default function* rootSaga() {
  yield all([
    takeEvery(actions.GET_SETTINGS, GET_SETTINGS),
    takeEvery(actions.UPDATE_SETTINGS, UPDATE_SETTINGS),
    takeEvery(actions.AI_LIST, AI_LIST),
    takeEvery(actions.UPDATE_AI_FEATURE, UPDATE_AI_FEATURE),
  ])
}
